import React from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import useIsPhone from "features/files/use-is-phone";
import {
  LEAVE_CALENDAR_HEADER_HEIGHT,
  LEAVE_CALENDAR_ROW_HEIGHT,
} from "./leaveCalendar.constants";
import type { CalendarUser } from "./leaveCalendar.types";

export interface UserMonthSummary {
  leave: number;
  remoteWork: number;
}

export interface LeaveCalendarUserPanelProps {
  users: CalendarUser[];
  summaries: Record<string, UserMonthSummary>;
  holidayCount: number;
  weekendHolidayCount: number;
  scrollRef: React.RefObject<HTMLDivElement>;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** "Jonas Petraitis" -> first name on one line, the rest on the next; fits the narrow phone column. */
function splitName(name: string) {
  const [firstName, ...rest] = name.trim().split(/\s+/);
  return { firstName, otherNames: rest.join(" ") };
}

function getGradientClass(name: string) {
  const gradients = [
    "from-rose-400 to-pink-500",
    "from-violet-400 to-indigo-500",
    "from-blue-400 to-cyan-500",
    "from-teal-400 to-emerald-500",
    "from-amber-400 to-orange-500",
    "from-indigo-400 to-purple-500",
    "from-fuchsia-400 to-pink-500",
    "from-emerald-400 to-teal-600",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % gradients.length;
  return gradients[index];
}

/**
 * Width follows the screen: a name only on a phone, the name and a one-line summary on a tablet, and the
 * e-mail with the four counters on a desktop.
 */
export function LeaveCalendarUserPanel({
  users,
  summaries,
  holidayCount,
  weekendHolidayCount,
  scrollRef,
  onScroll,
}: LeaveCalendarUserPanelProps) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const hh = LEAVE_CALENDAR_HEADER_HEIGHT;
  const rh = LEAVE_CALENDAR_ROW_HEIGHT;

  return (
    // The panel is 108 px wide on a phone, 220 px on a tablet and 300 px on a computer.
    <div className="flex h-full min-h-0 w-[108px] shrink-0 flex-col self-stretch border-r border-slate-200 bg-slate-50/50 md:w-[220px] lg:w-[300px]">
      <div
        className="flex shrink-0 items-end border-b border-slate-200/80 bg-slate-100/40 px-2 pb-3 pt-2 md:px-3"
        style={{ height: hh, minHeight: hh, boxSizing: "border-box" }}
      >
        <div className="flex-1">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 lg:text-[10px]">
            {t("leave_calendar_employee")}
          </span>
          {/* H and WO are the same for every employee: a tablet shows them once, here, not in every row. */}
          <div className="hidden text-[11px] font-bold text-slate-400 md:block lg:hidden">
            H {holidayCount} · WO {weekendHolidayCount}
          </div>
        </div>
        <div className="hidden gap-1 text-[9px] font-bold text-slate-400 lg:flex">
          <span className="w-8 text-center" title={t("leave_calendar_vacation")}>
            V
          </span>
          <span
            className="w-8 text-center"
            title={t("leave_calendar_type_remote_work", { defaultValue: "Remote work" })}
          >
            R
          </span>
          <span className="w-8 text-center" title={t("leave_calendar_holidays")}>
            H
          </span>
          <span className="w-10 text-center" title={t("leave_calendar_weekend_holidays")}>
            WO
          </span>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="leave-calendar-user-panel-scroll no-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
        onScroll={onScroll}
        style={{ scrollbarGutter: "stable" }}
      >
        <div style={{ minHeight: users.length * rh }}>
          {users.map((u) => {
            const s = summaries[u.id] || {
              leave: 0,
              remoteWork: 0,
            };
            return (
              <div
                key={u.id}
                className="flex items-center border-b border-slate-100/70 px-2 transition-colors hover:bg-white/80 group md:px-3"
                style={{ height: rh }}
                title={`${u.name}${u.email ? `\n${u.email}` : ""}`}
              >
                <div className="flex min-w-0 flex-1 items-center gap-1.5 pr-1 md:gap-2.5 md:pr-2">
                  <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br md:h-8 md:w-8 ${getGradientClass(u.name)} text-[11px] font-extrabold text-white shadow-md lg:text-[10px] ring-2 ring-white transition-transform duration-200 group-hover:scale-105`}>
                    {getInitials(u.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    {isPhone ? (
                      <PhoneName name={u.name} />
                    ) : (
                      <>
                        <div className="truncate text-xs font-extrabold text-slate-700 tracking-wide transition-colors group-hover:text-slate-900">
                          {u.name}
                        </div>
                        {u.email && (
                          <div className="mt-0.5 hidden truncate text-[9px] font-semibold text-slate-400 transition-colors group-hover:text-slate-500 lg:block">
                            {u.email}
                          </div>
                        )}
                        <div className="mt-0.5 truncate text-[11px] font-semibold text-slate-500 lg:hidden">
                          V {s.leave} · R {s.remoteWork}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="hidden gap-1 lg:flex">
                  <SummaryPill value={s.leave} variant="leave" />
                  <SummaryPill value={s.remoteWork} variant="remoteWork" />
                  <SummaryPill value={holidayCount} variant="holiday" />
                  <SummaryPill value={weekendHolidayCount} variant="weekendHoliday" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PhoneName({ name }: { name: string }) {
  const { firstName, otherNames } = splitName(name);
  return (
    <>
      <div className="truncate text-xs font-extrabold leading-tight text-slate-700">{firstName}</div>
      <div className="truncate text-[11px] font-semibold leading-tight text-slate-500">{otherNames}</div>
    </>
  );
}

function SummaryPill({
  value,
  variant,
}: {
  value: number;
  variant: "leave" | "remoteWork" | "holiday" | "weekendHoliday";
}) {
  return (
    <span
      className={clsx(
        "flex h-7 items-center justify-center rounded-lg px-1.5 text-[11px] font-bold tabular-nums border transition-all duration-200 hover:scale-105",
        variant === "leave" && "w-8 bg-emerald-50/60 border-emerald-100 text-emerald-700 shadow-sm shadow-emerald-100/10",
        variant === "remoteWork" && "w-8 bg-indigo-50/60 border-indigo-100 text-indigo-700 shadow-sm shadow-indigo-100/10",
        variant === "holiday" && "w-8 bg-amber-50/60 border-amber-100 text-amber-700 shadow-sm shadow-amber-100/10",
        variant === "weekendHoliday" && "w-10 bg-violet-50/60 border-violet-100 text-violet-700 shadow-sm shadow-violet-100/10"
      )}
    >
      {value}
    </span>
  );
}
